import { useEffect } from 'react';
import { useNodeEditorStore } from '@/stores/nodeEditorStore';
import { useWorkflowStore } from '@/stores/workflowStore';
import { FeatureErrorBoundary } from '@/components/ErrorBoundary';
import { NodeEditorModal } from './NodeEditorModal';
import { NodeEditorDrawer } from './NodeEditorDrawer';
import { NodeEditorHeader } from './NodeEditorHeader';
import { ExpandedView } from './expanded/ExpandedView';
import { NodeEditorDrawerContent } from './expanded/NodeEditorDrawerContent';
import { useEditorRunStore } from '@/stores/editorRunStore';
import { useDraftModeStore, selectIsReadOnly } from '@/stores/draftModeStore';
import { isMcpAgentTool } from '@/types/workflowDefinition';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';

/**
 * NodeEditor - The main node configuration component
 * Renders as a centered modal with compact/expanded modes
 */
export const NodeEditor = () => {
  // Use individual selectors to avoid infinite re-render loops
  const isOpen = useNodeEditorStore((s) => s.isOpen);
  const node = useNodeEditorStore((s) => s.node);
  const openEditor = useNodeEditorStore((s) => s.openEditor);
  const closeEditor = useNodeEditorStore((s) => s.closeEditor);
  const setIsReadOnly = useNodeEditorStore((s) => s.setIsReadOnly);

  // Feature flag: the redesigned right-side drawer shell is the default node
  // configuration experience. The legacy centered modal is preserved behind the
  // `nodeEditorLegacyModal` flag for fallback / side-by-side comparison.
  // Behavior/logic is identical — only the container chrome differs.
  const useLegacyModal = useFeatureFlag('nodeEditorLegacyModal');

  // The redesigned drawer IA covers configuration only. Execution/monitoring
  // reuses the proven ExpandedView run-data layout, so the vertical-nav IA is
  // selected only for the drawer + non-execution combination.
  const isExecutionMode = useEditorRunStore((s) => s.isExecutionMode);

  // Sync read-only state from the draft-mode store. This is the single
  // source of truth that all field components and store mutations honor.
  // Read from the store directly (not via context) so this works regardless
  // of where the DraftModeProvider lives in the tree.
  const isReadOnly = useDraftModeStore(selectIsReadOnly);
  useEffect(() => {
    setIsReadOnly(isReadOnly);
  }, [isReadOnly, setIsReadOnly]);


  // Use individual selectors for workflow store as well
  const workflowSelectedNode = useWorkflowStore((s) => s.selectedNode);
  const getAction = useWorkflowStore((s) => s.getAction);
  const getTrigger = useWorkflowStore((s) => s.getTrigger);
  const getTool = useWorkflowStore((s) => s.getTool);

  // Sync with workflow selection - open editor when selectedNode changes
  useEffect(() => {
    if (workflowSelectedNode) {
      // Check if we need to open or re-open the editor:
      // 1. Editor not open yet → open it
      // 2. Editor open but for a different node → re-open with new node data
      //    (uses getState() to avoid adding node to dependency array)
      const currentNode = useNodeEditorStore.getState().node;
      const currentNodeId = currentNode?.id;
      const needsOpen = !isOpen || currentNodeId !== workflowSelectedNode.id;

      // Update scopeContext even if the editor is already open for this node
      // (scopeContext may not have been available on first open)
      // Use value comparison since scopeContext is a new object reference each render
      const scopeChanged = !needsOpen && currentNode &&
        (workflowSelectedNode.scopeContext?.parentActionName !== currentNode.scopeContext?.parentActionName ||
         workflowSelectedNode.scopeContext?.branchType !== currentNode.scopeContext?.branchType);
      if (scopeChanged) {
        useNodeEditorStore.setState((state) => {
          if (state.node) {
            if (workflowSelectedNode.scopeContext) {
              state.node.scopeContext = workflowSelectedNode.scopeContext;
            } else {
              delete state.node.scopeContext;
            }
          }
        });
      }

      if (needsOpen) {
        let nodeData;

        if (workflowSelectedNode.type === 'trigger') {
          nodeData = getTrigger(workflowSelectedNode.name);
        } else if (workflowSelectedNode.type === 'action') {
          nodeData = getAction(workflowSelectedNode.name);
        } else if (workflowSelectedNode.type === 'tool' && workflowSelectedNode.parentAgentName && workflowSelectedNode.toolName) {
          // For tools, get the tool data from the parent agent
          const toolData = getTool(workflowSelectedNode.parentAgentName, workflowSelectedNode.toolName);

          if (toolData) {
            // MCP tools have no inner action — render the MCP config surface
            // using the tool itself as `nodeData`. The MCP tab reads
            // `inputs.connectionReference` and `inputs.parameters.allowedTools`
            // directly from this object.
            if (isMcpAgentTool(toolData)) {
              const editorNode: Parameters<typeof openEditor>[0] = {
                id: workflowSelectedNode.id,
                name: workflowSelectedNode.name,
                type: workflowSelectedNode.type,
                nodeData: toolData as unknown as Parameters<typeof openEditor>[0]['nodeData'],
                parentAgentName: workflowSelectedNode.parentAgentName,
                toolName: workflowSelectedNode.toolName,
                toolData,
                isMcpTool: true,
              };
              openEditor(editorNode);
              return;
            }

            // Extract the first inner action from tool.actions for Parameters/Settings tabs
            const innerActions = toolData.actions ?? {};
            const innerActionNames = Object.keys(innerActions);
            const firstInnerActionName = innerActionNames[0];
            const firstInnerAction = firstInnerActionName ? innerActions[firstInnerActionName] : undefined;

            if (firstInnerAction && firstInnerActionName) {
              // Store the inner action as nodeData so Parameters/Settings work
              nodeData = firstInnerAction;

              // Build the editor node with both tool and inner action data
              const editorNode: Parameters<typeof openEditor>[0] = {
                id: workflowSelectedNode.id,
                name: workflowSelectedNode.name,
                type: workflowSelectedNode.type,
                nodeData,
                parentAgentName: workflowSelectedNode.parentAgentName,
                toolName: workflowSelectedNode.toolName,
                toolData: toolData, // Store tool metadata for Tool tab
                innerActionName: firstInnerActionName, // Store inner action name for saving
              };

              openEditor(editorNode);
              return; // Early return since we handled it
            }
          }
        }

        if (nodeData) {
          const editorNode: Parameters<typeof openEditor>[0] = {
            id: workflowSelectedNode.id,
            name: workflowSelectedNode.name,
            type: workflowSelectedNode.type,
            nodeData,
          };
          // Only add optional properties if they have values
          if (workflowSelectedNode.parentAgentName) {
            editorNode.parentAgentName = workflowSelectedNode.parentAgentName;
          }
          if (workflowSelectedNode.toolName) {
            editorNode.toolName = workflowSelectedNode.toolName;
          }
          if (workflowSelectedNode.scopeContext) {
            editorNode.scopeContext = workflowSelectedNode.scopeContext;
          }
          openEditor(editorNode);
        }
      }
    } else if (!workflowSelectedNode && isOpen) {
      closeEditor();
    }
  }, [workflowSelectedNode, isOpen, getAction, getTrigger, getTool, openEditor, closeEditor]);

  // Don't render anything if not open
  if (!isOpen || !node) {
    return null;
  }

  // The drawer redesign covers node *configuration* only. Execution/monitoring
  // mode keeps the proven full-width modal + ExpandedView run-data layout — its
  // 3-panel layout can't fit the narrow drawer, so we never route it there.
  // The drawer is the default; the legacy modal is opt-in via the flag.
  const useDrawerUi = !useLegacyModal && !isExecutionMode;
  const Shell = useDrawerUi ? NodeEditorDrawer : NodeEditorModal;

  return (
    <FeatureErrorBoundary componentName="NodeEditor">
      <Shell>
        <NodeEditorHeader variant={useDrawerUi ? 'drawer' : 'modal'} />
        {useDrawerUi ? <NodeEditorDrawerContent /> : <ExpandedView />}
      </Shell>
    </FeatureErrorBoundary>
  );
};

NodeEditor.displayName = 'NodeEditor';
